/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Media from 'react-media';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';
import imgix from 'js/lib/imgix';

import { Button, Grid, PageGridContainer, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

import _t from 'i18n!nls/front-page';

export const CAROUSEL_SLIDE_HEIGHT = 500;
export const MOBILE_IMAGE_HEIGHT = 279;

const imgParams = {
  quality: 80,
};

const cPlusImgParams = { quality: 40 };

const styles = {
  section: (backgroundColor?: string | null) => css`
    background-color: ${backgroundColor ?? `var(--cds-color-neutral-background-primary)`};
  `,
  gridContainer: css`
    ${breakpoints.up('md')} {
      > div {
        margin: 0;
      }
    }

    ${breakpoints.down('sm')} {
      padding: 0 !important;
    }
  `,
  headerContainer: css`
    position: relative;
    justify-content: center;
    display: inline-flex;
    flex-direction: column;
    padding-left: var(--cds-spacing-800);
    height: ${CAROUSEL_SLIDE_HEIGHT}px;

    ${breakpoints.down('md')} {
      height: auto;
      width: 100%;
      padding-left: var(--cds-spacing-600);
    }

    ${breakpoints.down('sm')} {
      background-color: var(--cds-color-neutral-background-primary);
      padding: var(--cds-spacing-300);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200);
    }
  `,
  // Overrides d2 font-size temporarily.
  // TODO: Should be removed after upgrading to the next CDS version.
  header: css`
    ${breakpoints.up('lg')} {
      width: 90%;
    }

    && {
      font-size: 36px;
      line-height: 36px;

      ${breakpoints.up('md')} {
        ${typography2.titleLarge};
      }
    }
  `,
  description: css`
    ${typography2.bodyPrimary};
    margin: var(--cds-spacing-150) 0 var(--cds-spacing-300) 0;
  `,
  actionContainer: css`
    display: flex;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      justify-content: center;
    }
  `,
  cta: css`
    border-radius: var(--cds-border-radius-50);
    max-width: 100%;
  `,
  imgContainer: css`
    height: ${CAROUSEL_SLIDE_HEIGHT}px;
    width: 100%;

    ${breakpoints.down('sm')} {
      background-position: center;
      display: inline-flex;
      height: ${MOBILE_IMAGE_HEIGHT}px;
    }
  `,
  courseraPlusLogo: css`
    margin-bottom: var(--cds-spacing-200);

    ${breakpoints.down('md')} {
      margin-top: var(--cds-spacing-150);
    }
  `,
};

export type HeroCarouselSlideProps = {
  content?: Maybe<MarketingContentFieldsFragment>;
  secondaryButtonLabel?: string | null;
  secondaryButtonLink?: string | null;
  backgroundColor?: string | null;
  invertBody?: boolean;
  backgroundPosition?: string;
  slideTrackingName?: string;
  showCourseraPlusLogo?: boolean;
};

const HeroCarouselSlide = ({
  content,
  secondaryButtonLabel,
  secondaryButtonLink,
  backgroundColor,
  invertBody = false,
  backgroundPosition = 'center / contain',
  slideTrackingName = 'banner',
  showCourseraPlusLogo = false,
}: HeroCarouselSlideProps) => {
  const { description, callToActionLabel, callToActionLink, visual, title } = content || {};
  const userAgent = useUserAgent();
  // Visuals, description, and primary CTA info are required for this redesign
  if (!(visual && description && title && callToActionLabel)) {
    return null;
  }

  const heroImageSrc = visual?.url ? imgix.getOptimizedSrcs(visual?.url, 704, 500, imgParams).dpr2 : undefined;

  return (
    <TrackedDiv
      trackingName={slideTrackingName}
      withVisibilityTracking={true}
      requireFullyVisible={true}
      trackClicks={false}
      css={styles.section(backgroundColor)}
    >
      <PageGridContainer css={[styles.gridContainer, backgroundColor ? { backgroundColor: backgroundColor } : '']}>
        <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => (
            <Grid container css={matches ? { flexDirection: 'row' } : { flexDirection: 'row-reverse' }}>
              <Grid item md={6} xs={12} css={{ lineHeight: 0 }}>
                <div
                  css={[
                    styles.imgContainer,
                    heroImageSrc
                      ? {
                          background: `url(${heroImageSrc}) transparent no-repeat ${backgroundPosition};`,
                        }
                      : null,
                  ]}
                />
              </Grid>
              <Grid item md={6} xs={12} css={{ alignSelf: 'center' }}>
                <div css={styles.headerContainer}>
                  <SeoPageHeader />
                  {showCourseraPlusLogo && (
                    <ImgixWithPrioritizedLoading
                      css={styles.courseraPlusLogo}
                      src={invertBody && !matches ? logo.PLUS_FULL_WHITE : logo.PLUS_FULL_BLUE}
                      imgParams={cPlusImgParams}
                      alt={_t('Coursera Plus')}
                      maxHeight={16}
                      maxWidth={160}
                    />
                  )}
                  <Typography2
                    variant="titleMedium"
                    component="h2"
                    css={styles.header}
                    color={invertBody && !matches ? 'invertBody' : 'body'}
                  >
                    {title}
                  </Typography2>
                  <Typography2
                    variant="bodyPrimary"
                    component="p"
                    css={styles.description}
                    color={invertBody && !matches ? 'invertBody' : 'body'}
                  >
                    {description}
                  </Typography2>
                  <div css={styles.actionContainer}>
                    <Button
                      component={TrackedLink2}
                      requireFullyVisible={false}
                      withVisibilityTracking={false}
                      trackingName={`${slideTrackingName}_cta`}
                      css={styles.cta}
                      href={callToActionLink}
                      data-elementtiming="frontpage.banner.cta"
                      variant={invertBody && !matches ? 'primaryInvert' : 'primary'}
                    >
                      {callToActionLabel}
                    </Button>
                    {!!secondaryButtonLabel && (
                      <Button
                        component={TrackedLink2}
                        withVisibilityTracking={false}
                        requireFullyVisible={false}
                        trackingName="enterprise_cta_double_blue"
                        css={styles.cta}
                        href={secondaryButtonLink}
                        variant={invertBody && !matches ? 'primaryInvert' : 'secondary'}
                        role="link"
                      >
                        {secondaryButtonLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </Grid>
            </Grid>
          )}
        </Media>
      </PageGridContainer>
    </TrackedDiv>
  );
};

export default HeroCarouselSlide;
